/**
 * Fails fast with a readable message when the app is started without its environment, instead
 * of rendering a blank page (edge case 0.a).
 */
export interface Env {
  supabaseUrl: string;
  supabaseAnonKey: string;
  appEnv: 'local' | 'test' | 'production';
  /** Show the Google button; the provider itself is configured in Supabase Auth. */
  authGoogleEnabled: boolean;
  /** The build stamp bug reports carry (V13): the commit, or `dev` outside a build. */
  appVersion: string;
}

const REQUIRED = ['VITE_SUPABASE_URL', 'VITE_SUPABASE_ANON_KEY'] as const;

export class MissingEnvError extends Error {
  constructor(public readonly missing: string[]) {
    super(
      `Missing environment variables: ${missing.join(', ')}. Copy .env.example to .env at the repository root and fill them in (local values come from \`supabase start\`).`,
    );
    this.name = 'MissingEnvError';
  }
}

export function readEnv(source: Record<string, string | undefined> = import.meta.env): Env {
  const missing = REQUIRED.filter((k) => !source[k]);
  if (missing.length) throw new MissingEnvError(missing);
  const appEnv = source['VITE_APP_ENV'];
  return {
    supabaseUrl: source['VITE_SUPABASE_URL']!,
    supabaseAnonKey: source['VITE_SUPABASE_ANON_KEY']!,
    appEnv: appEnv === 'production' || appEnv === 'test' ? appEnv : 'local',
    authGoogleEnabled: source['VITE_AUTH_GOOGLE_ENABLED'] !== 'false',
    appVersion: source['VITE_APP_VERSION'] || 'dev',
  };
}
